import PDF from './pdf'
import { capitalizeFirstLetter } from '../lib/utils'

export default class PurchaseReport extends PDF {
  /**
   * Description
   * @param {import('jspdf').DocumentProperties} args
   */
  constructor(args = {}) {
    super()
    this.doc.setProperties({
      unit: 'mm',
      format: 'a4',
      ...args,
    })
    this.addFonts()
  }

  /**
   * Description
   * @param {HeadingData} data
   */
  __header({ storeName = '', dateRange = '' }) {
    const title = `${capitalizeFirstLetter(storeName)} Report`
    this.centerText({ fontSize: 24, text: title, yOffset: 15, color: [55, 71, 79] })
    this.centerText({ fontSize: 12, text: 'Purchases', yOffset: 22, color: [55, 71, 79] })
    this.centerText({ fontSize: 12, text: dateRange, yOffset: 27 })
    this.doc.setFillColor('#CFD8DC')
    this.doc.line(15, 30, 195, 30, 'F')
  }

  /**
   * Description
   * @param {HeadingData} subHeader
   * @returns {void}
   */
  __subHeader(subHeader) {
    const { totalAmount = '' } = subHeader || {}
    const [x, y] = [15, 40]
    const delimiter = ':'

    this.generateField({
      label: 'Total Amount',
      value: totalAmount,
      coordinates: [x, y],
      delimiter,
    })
  }

  /**
   * Eight columns have to fit A4 portrait (180mm), so the headers are
   * abbreviated and every column except Product is pinned to the width its
   * content needs — Product takes whatever is left and wraps.
   *
   * @param {{ totalAmount: number; }} params
   * @param {ReportItem[]} body
   * @returns {any}
   */
  __table(_, body) {
    this.doc.autoTable({
      head: [['Rcpt #', 'Product', 'Unit', 'Qty', 'Cost', 'Total', 'Amount', 'Date']],
      startY: 50,
      margin: { left: 15, right: 15 },
      styles: { fontSize: 7, cellPadding: 1.2, overflow: 'linebreak' },
      headStyles: { fillColor: [55, 71, 79], halign: 'center', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 14 },
        3: { cellWidth: 13, halign: 'right' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 20, halign: 'center' },
      },
      body: [
        ...body,
      ],
    })
  }

  /**
   * Description
   * @param {string} namePrefix
   * @returns {void}
   */
  __save(namePrefix) {
    const name = `${namePrefix}_purchase_report_${new Date().getTime()}`.replace(/[\s]+/g, '_')
    this.doc.save(name)
  }

  /**
   * Description
   * @param {HeadingData} data
   * @param {ReceiptItem[]} items
   * @returns {any}
   */
  generate(data, items) {
    this.__header(data)
    this.__subHeader(data)
    this.__table(data, items)
    this.__save(data.namePrefix)
  }
}

/**
 * @typedef HeadingData
 * @property {string} namePrefix
 * @property {string} storeName
 * @property {string} dateRange
 * @property {string} totalAmount
 *
 * @typedef ReportItem
 * @property {string} receipt_no
 * @property {string} product_name
 * @property {string} unit
 * @property {number} quantity
 * @property {number} cost
 * @property {number} total
 * @property {number} amount
 * @property {string} date_purchased
 */
